import React, { useEffect, useMemo } from 'react';
import useIntls from '@opm/hooks/useIntls';
import SvgIcon from '@home/components/SvgIcon';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import type { TGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { ITrendItemBase, ICreativeItem } from '../types';
import TrendInfoRows, {
  ITrendInfoKPIRowConfig,
} from './components/TrendInfoRows';
import type { TTopicTypeColorMap } from '../utils';
import AISummary from './components/AISummary';
import RelatedCreatives from './components/RelatedCreatives';
import './index.scss';
import CardCommon from '@op/components/CardCommon';

interface ITrendDrawerProps {
  /** 列表选中项（提供大部分 UI 数据） */
  record: ITrendItemBase | null;
  /** 含 (game_id, topic) + 时间窗 + channels 的 filter，透传给 RelatedCreatives */
  filter?: any;
  /** 游戏元信息字典（cond.materials_config_games）：id → meta */
  gameMap?: TGameMap;
  /** Topic Type chip 颜色映射（按 L1 parent 取色，L2 共享 parent 色） */
  topicTypeColorMap?: TTopicTypeColorMap;
  onClose: () => void;
  onViewAll?: (record: ITrendItemBase) => void;
  onTitleClick?: (record: ITrendItemBase) => void;
  onThumbnailClick?: (item: ICreativeItem, record: ITrendItemBase) => void;
  /** KPI 行配置（透传给 TrendInfoRows，默认 CreativeTrends 三件套） */
  kpiConfigs?: ITrendInfoKPIRowConfig[];
}

/**
 * Trend Detail Drawer 右侧 inline 侧边栏（按 Figma 3902_62017 设计）
 *
 * 结构：
 *  - Header（固定）：Trend Detail 标题 + 关闭 ×
 *  - Body（可滚动，3 个区块）：
 *      1. Trend Info label-value 列表（标题 + Game/Total Views/Creatives/Avg.Days/Type&Elements）
 *      2. AI Summary 渐变块
 *      3. Related Creatives 2 列网格（12 个）
 *
 * 数据分层：
 *  - 标题 / KPI 数值 / Type&Elements / AI Summary → 来自 record（列表项）
 *  - Related Creatives → 独立请求
 */
export default function TrendDrawer({
  record,
  filter,
  gameMap,
  topicTypeColorMap,
  onClose,
  onViewAll,
  onTitleClick,
  onThumbnailClick,
  kpiConfigs,
}: ITrendDrawerProps) {
  const { intl, isEn } = useIntls();

  useEffect(() => {
    if (record?.topic) {
      DBOM_POINT.btn('drawer_view_all', {
        game_id: record.game_id,
        topic: record.topic,
      });
    }
  }, [record?.game_id, record?.topic]);

  const relatedFilter = useMemo(() => {
    if (filter) {
      return {
        ...filter,
        group_by: 'video_url',
      };
    }
    return null;
  }, [filter]);

  if (!record) return null;

  // topic：后端按多语言下发 topic（zh）/ topic_en（en），按当前语言取值
  const title = isEn ? record.topic_en || record.topic : record.topic;

  return (
    <CardCommon className="dbom-ct-content__drawer">
      <div className="dbom-ct-drawer">
        {/* Header：吸顶 */}
        <div className="dbom-ct-drawer__header">
          <span className="dbom-ct-drawer__header-title">
            {intl('Trend Detail')}
          </span>
          <div
            className="dbom-ct-drawer__close"
            onClick={onClose}
            title={intl('Close')}
          >
            <SvgIcon type="clonse" />
          </div>
        </div>

        {/* Body：滚动区 */}
        <div className="dbom-ct-drawer__body">
          {/* Section 1: Trend Info */}
          <div className="dbom-ct-drawer__section">
            <div
              className="dbom-ct-drawer__trend-title"
              onClick={(e) => {
                e.stopPropagation();
                onTitleClick?.(record);
              }}
              title={title}
            >
              {title || '-'}
              <SvgIcon type="arrow-right" className="card-slot-icon" />
            </div>
            <TrendInfoRows
              record={record}
              gameMap={gameMap}
              kpiConfigs={kpiConfigs}
              topicTypeColorMap={topicTypeColorMap}
            />
          </div>

          {/* Section 2: AI Summary */}
          <div className="dbom-ct-drawer__section">
            <AISummary record={record} />
          </div>

          {/* Section 3: Related Creatives（独立调 /related_creatives 接口） */}
          <div className="dbom-ct-drawer__section dbom-ct-drawer__section--last">
            <RelatedCreatives
              filter={relatedFilter}
              onViewAll={() => onViewAll?.(record)}
              onThumbnailClick={(item) => onThumbnailClick?.(item, record)}
              title={'Related Videos'}
            />
          </div>
        </div>
      </div>
    </CardCommon>
  );
}
